import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';
import TableContainer from '../../Components/Common/TableContainer';
import DeleteModal from '../../Components/Common/DeleteModal';
import Loader from '../../Components/Common/Loader';
import { getUsersQuery } from '../../queries';
import { phoneNumberFormatter } from '../../slices/phone-number-formatter';
import { createUsers, deleteUser, editUser, assignRole, unassignRole } from '../../api/users';
import 'react-toastify/dist/ReactToastify.css';

const Users = () => {
  const [passwordShow, setPasswordShow] = useState(false);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [user, setUser] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: users } = useQuery({
    ...getUsersQuery({
      page,
      orderBy: 'Id',
      order: 'desc',
      // roleIds removed to show all users
    }),
  });

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setUser(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: (user && user.firstName) || '',
      lastName: (user && user.lastName) || '',
      phoneNumber: (user && user.phoneNumber) || '+998',
      password: user && user.password,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('This field is required')),
      lastName: Yup.string().required(t('This field is required')),
      phoneNumber: Yup.string()
        .required(t('This field is required'))
        .test('valid-phone', t('Invalid phone number'), function (value) {
          const digits = value?.replace(/\D/g, '');
          return digits && /^998\d{9}$/.test(digits);
        }),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateuser = {
          id: user.id,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber.replace(/\D/g, ''),
        };
        editUser(user.id, updateuser)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.clear();
            toggle();
          })
          .catch((err) => {
            if (err?.data.error?.message) {
              alert(err?.data.error?.message);
            }
          });
        validation.resetForm();
      } else {
        const newuser = {
          firstName: values['firstName'],
          lastName: values['lastName'],
          phoneNumber: values['phoneNumber'].replace(/\D/g, ''),
          password: values['password'],
          confirmPassword: values['password'],
        };
        createUsers(newuser)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.clear();
            toggle();
          })
          .catch((err) => {
            if (err?.data.error?.message) {
              alert(err?.data.error?.message);
            }
          });
        validation.resetForm();
      }
    },
  });

  const onDelete = () => {
    deleteUser(deleteModal)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      })
      .catch((err) => {
        if (err?.data.error?.message) {
          alert(err?.data.error?.message);
        }
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  const handleEdit = (user) => {
    setUser(user);
    setIsEdit(true);
    toggle();
  };

  /* Assign Role Modal State */
  const [assignRoleModal, setAssignRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('user'); // Default to User in Users table

  const toggleAssignRoleModal = useCallback(() => {
    if (assignRoleModal) {
      setAssignRoleModal(false);
      setUser(null);
      setSelectedRole('user');
    } else {
      setAssignRoleModal(true);
    }
  }, [assignRoleModal]);

  const handleAssignRole = (user) => {
    setUser(user);
    if (user.roles && user.roles.length > 0) {
      // If user has roles, try to select one of them or default to 'user'
      if (user.roles.some((r) => r.id === 1)) setSelectedRole('1');
      else if (user.roles.some((r) => r.id === 2)) setSelectedRole('2');
      else setSelectedRole('user');
    } else {
      setSelectedRole('user');
    }
    toggleAssignRoleModal();
  };

  const onAssignRoleSubmit = () => {
    if (!user || !user.id) return;

    // First unassign all relevant roles (1 and 2)
    unassignRole({ userId: user.id, roleIds: [1, 2] })
      .then(() => {
        // If selected role is 'user', we are done (roles unassigned)
        if (selectedRole === 'user') {
          queryClient.invalidateQueries({ queryKey: ['users'] });
          toggleAssignRoleModal();
          return;
        }

        // Otherwise, assign the new role
        assignRole({ userId: user.id, roleIds: [parseInt(selectedRole)] })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toggleAssignRoleModal();
          })
          .catch((err) => {
            if (err?.data?.error?.message) {
              alert(err?.data.error.message);
            }
          });
      })
      .catch((err) => {
        // Even if unassign fails (e.g. user didn't have roles), try to assign if not 'user'
        // But best to show error if it's a real error
        if (err?.data?.error?.message) {
          // alert(err?.data.error.message);
          // silently continue or alert?
          // Let's continue to assign if possible, but alerting is safer for debugging.
          // For now, let's proceed to assign if unassign failed (maybe clean slate wasn't possible or needed)
        }

        if (selectedRole !== 'user') {
          assignRole({ userId: user.id, roleIds: [parseInt(selectedRole)] })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['users'] });
              toggleAssignRoleModal();
            })
            .catch((assignErr) => {
              if (assignErr?.data?.error?.message) {
                alert(assignErr?.data.error.message);
              }
            });
        } else {
          queryClient.invalidateQueries({ queryKey: ['users'] });
          toggleAssignRoleModal();
        }
      });
  };

  const onDeleteRole = (roleId) => {
    if (!user || !user.id || !roleId) return;
    if (window.confirm(t('Are you sure you want to remove this role?'))) {
      unassignRole({ userId: user.id, roleIds: [roleId] })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['users'] });
          toggleAssignRoleModal();
        })
        .catch((err) => {
          if (err?.data?.error?.message) {
            alert(err?.data.error.message);
          }
        });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t('First name'),
        accessorKey: 'firstName',
        enableColumnFilter: false,
      },
      {
        header: t('Last name'),
        accessorKey: 'lastName',
        enableColumnFilter: false,
      },
      {
        header: t('Phone'),
        enableColumnFilter: false,
        cell: ({ row }) => phoneNumberFormatter(row.original.phoneNumber),
      },
      {
        header: t('Actions'),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-info"
              onClick={() => handleAssignRole(row.original)}
              title={t('Manage Roles')}
            >
              <i className="ri-user-settings-line"></i>
            </button>
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: '10px' }}
              onClick={() => handleEdit(row.original)}
            >
              <i className="ri-edit-fill"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: '10px' }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    [t],
  );

  document.title = 'Users | Bazaar';

  return (
    <div className="page-content">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      {/* Assign Role Modal */}
      <Modal isOpen={assignRoleModal} toggle={toggleAssignRoleModal} centered>
        <ModalHeader toggle={toggleAssignRoleModal}>{t('Manage Roles')}</ModalHeader>
        <ModalBody>
          {user?.roles && user.roles.length > 0 && (
            <div className="mb-4">
              <h6>{t('Current Roles')}:</h6>
              <ul className="list-group">
                {user.roles.map((role) => (
                  <li
                    key={role.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    {role.name}
                    <button className="btn btn-sm btn-danger" onClick={() => onDeleteRole(role.id)}>
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mb-3">
            <Label for="role-select" className="form-label">
              {t('Assign New Role')}
            </Label>
            <Input
              type="select"
              id="role-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="1">Admin</option>
              <option value="2">Agent</option>
              <option value="user">User</option>
            </Input>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-light" onClick={toggleAssignRoleModal}>
            {t('Close')}
          </button>
          <button className="btn btn-success" onClick={onAssignRoleSubmit}>
            {t('Assign')}
          </button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="userList">
              <CardHeader className="border-0 ">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0 fw-bold">{t('Users')}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        onClick={() => {
                          setIsEdit(false);
                          toggle();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> {t('create')}
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <div className="py-2"></div>
              <div className="card-body pt-0">
                <div>
                  {users?.data.length ? (
                    <TableContainer
                      pagination={{
                        setPage: setPage,
                        page: page,
                        total: users?.pagination.TotalPages,
                      }}
                      columns={columns}
                      data={users?.data || []}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted"
                    />
                  ) : (
                    <Loader />
                  )}
                </div>

                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? t('edit') : t('create')}
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <input type="hidden" id="id-field" />

                      <div className="mb-3" id="modal-id" style={{ display: 'none' }}>
                        <Label htmlFor="id-field1" className="form-label">
                          ID
                        </Label>
                        <Input
                          type="text"
                          id="id-field1"
                          className="form-control"
                          placeholder="ID"
                          readOnly
                        />
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="firstName-field" className="form-label">
                          {t('First name')}
                        </Label>
                        <Input
                          name="firstName"
                          id="firstName-field"
                          className="form-control"
                          placeholder={t('First name')}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firstName || ''}
                          invalid={
                            validation.touched.firstName && validation.errors.firstName
                              ? true
                              : false
                          }
                        />
                        {validation.touched.firstName && validation.errors.firstName ? (
                          <FormFeedback type="invalid">{validation.errors.firstName}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="lastName-field" className="form-label">
                          {t('Last name')}
                        </Label>
                        <Input
                          name="lastName"
                          id="lastName-field"
                          className="form-control"
                          placeholder={t('Last name')}
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.lastName || ''}
                          invalid={
                            validation.touched.lastName && validation.errors.lastName ? true : false
                          }
                        />
                        {validation.touched.lastName && validation.errors.lastName ? (
                          <FormFeedback type="invalid">{validation.errors.lastName}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="phoneNumber-field" className="form-label">
                          {t('Phone')}
                        </Label>
                        <InputMask
                          mask="+998 99 999-99-99"
                          maskChar={null}
                          value={validation.values.phoneNumber}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        >
                          {(inputProps) => (
                            <Input
                              {...inputProps}
                              name="phoneNumber"
                              type="text"
                              id="phoneNumber-field"
                              placeholder="+998 99 999 99 99"
                              className="form-control"
                              invalid={
                                validation.touched.phoneNumber && validation.errors.phoneNumber
                                  ? true
                                  : false
                              }
                            />
                          )}
                        </InputMask>
                        {validation.touched.phoneNumber && validation.errors.phoneNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.phoneNumber}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {!user?.id && (
                        <div className="mb-3">
                          <Label className="form-label" htmlFor="password-input">
                            {t('Password')}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password}
                              type={passwordShow ? 'text' : 'password'}
                              className="form-control pe-5"
                              placeholder={t('Password')}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!validation.errors.password}
                            />
                            {validation.errors.password && (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            )}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>
                      )}
                    </ModalBody>
                    <ModalFooter>
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setModal(false);
                          }}
                        >
                          {t('Close')}
                        </button>

                        <button type="submit" className="btn btn-success">
                          {!!isEdit ? t('Update') : t('create')}{' '}
                        </button>
                      </div>
                    </ModalFooter>
                  </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Users;
